import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import {Button, Form, Header, Message} from 'semantic-ui-react';
import withLifecycles from 'async-lifecycles';
import produce from 'immer';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import {FormattedMessage} from '../i18n/Context';
import AppFooter from '../layout/AppFooter';
import {FormWrapper, Wrapper} from './Layout';
import api from '../../store/services/api';
import {ErrorMessages} from '../form-components/ErrorMessages';
import Turnstile from '../form-components/Turnstile';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

// Pushed off screen rather than display:none. Scripted signups fill anything that
// looks like a field, and a visitor never sees or tabs into it.
const honeypotStyle = {
  position: 'absolute',
  left: '-5000px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
};

export class SignUp extends Component {
  state = {
    user: {
      emailAddress: '',
      firstName: '',
      lastName: '',
      company: '',
      title: '',
      phoneNumber: '',
      associationRequested: false,
      website: '',
    },
    turnstileToken: '',
    error: {},
  };
  _turnstile = React.createRef();
  _mutateState = mutationFn => {
    this.setState(produce(mutationFn));
  };
  _handleInputChanged = (event, {name, value}) => {
    this._mutateState(state => {
      state.user[name] = value;
    });
  };
  _handleCheckChanged = (event, {name, checked}) => {
    this._mutateState(state => {
      state.user[name] = checked;
    });
  };
  _handleTurnstileVerified = turnstileToken => {
    this.setState({turnstileToken});
  };
  _signup = () => {
    withLifecycles(
      () => {
        return api.post('/users/sign-up', {
          ...this.state.user,
          turnstileToken: this.state.turnstileToken,
        });
      },
      ({loading, data, error}) => {
        this.setState({loading, data, error});

        // A token is single use. Once the request comes back with an error the visitor
        // needs a fresh challenge before they can submit again.
        if (error && this._turnstile.current) {
          this._turnstile.current.reset();
        }
      },
    );
  };

  render() {
    const {
      data,
      loading,
      error,
      turnstileToken,
      user: {
        emailAddress,
        firstName,
        lastName,
        company,
        associationRequested,
        title,
        phoneNumber,
        website,
      },
    } = this.state;

    // Where no site key is configured (local dev) the widget never renders, so the
    // form must not be permanently un-submittable.
    const awaitingTurnstile = !!TURNSTILE_SITE_KEY && !turnstileToken;

    return (
      <Wrapper>
        <Header textAlign="center">
          <img
            src={`${process.env.PUBLIC_URL}/mm_logo.png`}
            alt="M&M International"
          />
        </Header>
        <FormWrapper attached loading={loading}>
          <Header textAlign="center">
            <FormattedMessage id="signup.header" />
          </Header>
          {data && data.result && !data.hasErrors && !data.result.isDomainRestricted && (
            <Message success>
              <FormattedMessage id="signup.form.button.checkEmail" />
            </Message>
          )}
          {data && data.result && !data.hasErrors && data.result.isDomainRestricted && (
            <Message success>
              <FormattedMessage id="signup.form.button.getAssistance" />
            </Message>
          )}
          {(!data || data.hasErrors) && (
            <Form onSubmit={this._signup}>
              <Form.Field>
                <label>
                  <FormattedMessage id="signup.form.emailAddress" />
                </label>
                <Form.Input
                  type="email"
                  name="emailAddress"
                  value={emailAddress}
                  onChange={this._handleInputChanged}
                  autoFocus
                />
              </Form.Field>
              <Form.Field>
                <label>
                  <FormattedMessage id="signup.form.firstName" />
                </label>
                <Form.Input
                  type="text"
                  name="firstName"
                  value={firstName}
                  onChange={this._handleInputChanged}
                />
              </Form.Field>
              <Form.Field>
                <label>
                  <FormattedMessage id="signup.form.lastName" />
                </label>
                <Form.Input
                  type="text"
                  name="lastName"
                  value={lastName}
                  onChange={this._handleInputChanged}
                />
              </Form.Field>
              <Form.Field>
                <label>
                  <FormattedMessage id="signup.form.company" />
                </label>
                <Form.Input
                  type="text"
                  name="company"
                  value={company}
                  onChange={this._handleInputChanged}
                />
              </Form.Field>
              <Form.Checkbox
                label="Request Access to Customer Orders and Parts"
                name="associationRequested"
                checked={associationRequested}
                onChange={this._handleCheckChanged}
              />
              <Form.Field>
                <label>
                  <FormattedMessage id="signup.form.title" />
                </label>
                <Form.Input
                  type="text"
                  name="title"
                  value={title}
                  onChange={this._handleInputChanged}
                />
              </Form.Field>
              <Form.Field
                type="text"
                name="phoneNumber"
                value={phoneNumber}
                onChange={value =>
                  this._handleInputChanged(null, {
                    name: 'phoneNumber',
                    value: value,
                  })
                }
                control={PhoneInput}
                inputStyle={{paddingLeft: 50, height: 40}}
                preferredCountries={['us']}
                country={'us'}
                disableAreaCodes
              >
                <label>
                  <FormattedMessage id="signup.form.phoneNumber" />
                </label>
              </Form.Field>
              <div style={honeypotStyle} aria-hidden="true">
                <label htmlFor="website">
                  Leave this field blank
                </label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={website}
                  onChange={event =>
                    this._handleInputChanged(null, {
                      name: 'website',
                      value: event.target.value,
                    })
                  }
                  tabIndex={-1}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
              </div>
              {TURNSTILE_SITE_KEY && (
                <Form.Field>
                  <Turnstile
                    ref={this._turnstile}
                    siteKey={TURNSTILE_SITE_KEY}
                    onVerify={this._handleTurnstileVerified}
                  />
                </Form.Field>
              )}
              <ErrorMessages error={error} />
              <div className="stacked-buttons">
                <Button type="submit" primary fluid disabled={awaitingTurnstile}>
                  <FormattedMessage id="signup.form.button.signup" />
                </Button>

                <Button as={Link} to="/account/login" fluid className="clear">
                  <FormattedMessage id="signup.form.button.returnToLogin" />
                </Button>
              </div>
            </Form>
          )}
        </FormWrapper>
        <AppFooter />
      </Wrapper>
    );
  }
}

export default SignUp;
